
import React, { useState } from "react";
import { useAuth } from "@/contexts/auth";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { SwapRequest } from "@/services/myReagents";
import NotLoggedInMessage from "@/components/auth/NotLoggedInMessage";
import { Beaker, Archive, Repeat } from "lucide-react";

// Import components
import PageHeader from "@/components/myReagents/PageHeader";
import ErrorDisplay from "@/components/myReagents/ErrorDisplay";
import TabNavigation, { TabItem } from "@/components/myReagents/TabNavigation";
import UserReagentsTable from "@/components/myReagents/UserReagentsTable";
import RemovedReagentsList from "@/components/myReagents/RemovedReagentsList";
import SwapRequestsTable from "@/components/myReagents/SwapRequestsTable";

// Import our new custom hook
import { useMyReagents } from "@/hooks/useMyReagents";

const MyReagentsPage: React.FC = () => {
  const { user } = useAuth();
  
  // Use our custom hook for reagent management
  const {
    myReagents,
    removedReagents,
    swapRequests,
    loadingReagents,
    loadingRemovedReagents,
    loadingRequests,
    reactivatingId,
    error,
    pendingRequestsCount,
    removedReagentsCount,
    loadMyReagents,
    loadSwapRequests,
    handleReactivateReagent,
    refreshData
  } = useMyReagents();
  
  const [activeTab, setActiveTab] = useState("reagents");
  
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    
    if (value === "requests" && swapRequests.length === 0 && !loadingRequests) {
      // Only reload if we don't have data and aren't already loading
      loadSwapRequests();
    } else if (value === "reagents" && myReagents.length === 0 && !loadingReagents) {
      // Only reload if we don't have data and aren't already loading
      loadMyReagents();
    } else if (value === "removed" && removedReagents.length === 0 && !loadingRemovedReagents) {
      // Only reload if we don't have data and aren't already loading
      loadMyReagents();
    }
  };
  
  const tabs: TabItem[] = [
    {
      value: "reagents",
      label: "Available",
      icon: Beaker,
      count: myReagents.length
    },
    {
      value: "removed",
      label: "Removed",
      icon: Archive,
      badge: {
        count: removedReagentsCount,
        variant: "secondary"
      }
    },
    {
      value: "requests",
      label: "Swap Requests",
      icon: Repeat,
      badge: {
        count: pendingRequestsCount,
        variant: "primary"
      }
    }
  ];
  
  if (!user) {
    return <NotLoggedInMessage message="Please log in to view your reagents" />;
  }
  
  return (
    <div className="container py-8">
      <PageHeader title="My Reagents" onRefresh={refreshData} />
      
      <ErrorDisplay error={error} />
      
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabNavigation 
          tabs={tabs}
          value={activeTab}
          onValueChange={handleTabChange}
        />
        
        <TabsContent 
          value="reagents"
          className="animate-fade-in"
        >
          <UserReagentsTable 
            reagents={myReagents} 
            isLoading={loadingReagents} 
            onReagentDeleted={loadMyReagents} 
          />
        </TabsContent>
        
        <TabsContent 
          value="removed"
          className="animate-fade-in"
        >
          <RemovedReagentsList
            reagents={removedReagents}
            isLoading={loadingRemovedReagents}
            reactivatingId={reactivatingId}
            onReactivate={handleReactivateReagent}
          />
        </TabsContent>
        
        <TabsContent 
          value="requests"
          className="animate-fade-in"
        >
          <SwapRequestsTable 
            swapRequests={swapRequests} 
            isLoading={loadingRequests} 
            onSwapRequestUpdated={loadSwapRequests} 
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MyReagentsPage;
